import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { COINS, NOTES, REST_LOGIN_ERROR_TYPES, ROLE, SWAL_SETTINGS } from "../../../utils/Constants";
import { useTranslation } from "react-i18next";
import { add } from "../services/cash-register.service";
import { DT, getLocalKey, handleNavigation, handleServerValidations, isValidJSON, setLocalKey } from "../../../utils/commonfunction";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { setCashRegister } from "../../../redux/slices/cashierDetail";
import { useNavigate } from "react-router-dom";

export default function PenominationDetails() {
  const { t } = useTranslation();
  const restaurantData = getLocalKey(ROLE.CASHIER) ? JSON.parse(getLocalKey(ROLE.CASHIER)) : {};
  const cashierDetails = useSelector((state) => state?.cashierDetails?.data);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Function to calculate the total value of notes and coins

  const isCashRegisterDone = useSelector((state) => state?.cashierDetails?.cashRegister);

  useEffect(() => {
    try {
      if (isCashRegisterDone !== false) {
        navigate("/cashier/dashboard");
      }
    } catch (error) {
      console.error("Error parsing or handling cashier data:", error);
    }
  }, [isCashRegisterDone]);

  const calculateTotal = (values) => {
    const totalNotes = Object.keys(values.notes).reduce((sum, key) => {
      return sum + values.notes[key] * parseInt(key, 10);
    }, 0);

    const totalCoins = Object.keys(values.coins).reduce((sum, key) => {
      return sum + values.coins[key] * parseInt(key, 10);
    }, 0);

    return totalNotes + totalCoins;
  };

  const notesShape = NOTES.reduce((acc, item) => {
    acc[item.value] = Yup.number().integer(t("err_integer_only")).min(0, t("err_min_note_value"));
    return acc;
  }, {});

  const coinsShape = COINS.reduce((acc, item) => {
    acc[item.value] = Yup.number().integer(t("err_integer_only")).min(0, t("err_min_coin_value"));
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: {
      openingBalance: 0,
      notes: NOTES.reduce((acc, item) => {
        // acc[item.value] = 0;
        return acc;
      }, {}),
      coins: COINS.reduce((acc, item) => {
        // acc[item.value] = 0;
        return acc;
      }, {}),
    },
    validationSchema: Yup.object({
      openingBalance: Yup.number().required(t("err_opening_balance")).min(0, t("err_min_opening_balance")),
      notes: Yup.object().shape(notesShape),
      coins: Yup.object().shape(coinsShape),
    }),
    validate: (values) => {
      const errors = {};
      const total = calculateTotal(values);
      if (values.openingBalance !== total) {
        errors.openingBalance = DT(t("msg_amount_greater_than_total"), [total]);
      }
      // const hasNotesValue = Object.values(values.notes).some(v => v > 0);
      // const hasCoinsValue = Object.values(values.coins).some(v => v > 0);

      // if (!hasNotesValue && !hasCoinsValue) {
      //   errors.notes = t('minimum_note_coin_err');
      // }

      return errors;
    },
    onSubmit: (values) => {
      let obj = {
        opening_balance: values.openingBalance,
        opening_notes: isValidJSON(values.notes) ? JSON.stringify(values.notes) : null,
        opening_coins: isValidJSON(values.coins) ? JSON.stringify(values.coins) : null,
        cashier_id: cashierDetails?._id,
      };
      add(obj)
        .then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            setLocalKey("cashRegisterEntry", true);
            dispatch(setCashRegister(true));
            handleNavigation({
              successURL: "/cashier/dashboard",
              role: ROLE.CASHIER,
            });
          } else {
            let message = handleServerValidations(response);
            /** Handling different types of server errors */
            const supportUrlConditions = [
              REST_LOGIN_ERROR_TYPES.ACCOUNT_IS_BLOCKED,
              REST_LOGIN_ERROR_TYPES.SUBSCRIPTION_EXPIRED,
              REST_LOGIN_ERROR_TYPES.ACCOUNT_NOT_ENROLLED,
              REST_LOGIN_ERROR_TYPES.PASSWORD_NOT_SET_YET,
            ];
            if (supportUrlConditions.indexOf(response.error_type) !== -1) {
              message += `<a className="d-block mt-2 no-decoration link-text" href="/support/${response.error_type}">${t("btn_text_contact_us")}</a>`;
            }
            Swal.fire({
              icon: "error",
              html: message,
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          console.error("error===>", error);
        });
    },
  });

  // Update the opening balance when notes or coins change
  useEffect(() => {
    const total = calculateTotal(formik.values);
    formik.setFieldValue("openingBalance", total);
  }, [formik.values.notes, formik.values.coins]);

  // Handling changes to input fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, Number(value));
  };

  // Function to generate table rows
  const generateTableRows = () => {
    const rows = [];
    const maxRows = Math.max(NOTES.length, COINS.length);
    for (let i = 0; i < maxRows; i++) {
      const note = NOTES[i] || { value: "", label: "" };
      const coin = COINS[i] || { value: "", label: "" };
      rows.push(
        <tr key={i}>
          <td>
            {note.label && (
              <div className="d-flex align-items-center justify-content-between">
                <span className="fw-500">{note.label}</span>
                <input
                  className="form-control no_spring_input"
                  name={`notes.${note.value}`}
                  type="text" // ← change this from "number" to "text"
                  inputMode="numeric" // ← still shows numeric keyboard on mobile
                  value={formik.values.notes[note.value] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow empty input or numeric only
                    if (val === "" || /^\d+$/.test(val)) {
                      formik.setFieldValue(`notes.${note.value}`, val === "" ? "" : parseInt(val, 10));
                    }
                  }}
                />
              </div>
            )}
          </td>
          <td>
            {coin.label && (
              <div className="d-flex align-items-center justify-content-between">
                <span className="fw-500">{coin.label}</span>
                <input
                  className="form-control no_spring_input"
                  name={`coins.${coin.value}`}
                  type="text"
                  inputMode="numeric"
                  value={formik.values.coins[coin.value] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+$/.test(val)) {
                      formik.setFieldValue(`coins.${coin.value}`, val === "" ? "" : parseInt(val, 10));
                    }
                  }}
                />
              </div>
            )}
          </td>
        </tr>
      );
    }
    return rows;
  };

  // Function to format date as YYYY/MM/DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  return (
    <div className="dashboard-cashier-wrap">
      <div className="penomination-details-wrap">
        <div className="penomination-details-box">
          <div className="card">
            <div className="card-body">
              <div className="card-header">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="fw-500">{t("input_placeholder_date")}</span>
                  <span className="fw-500">{formatDate(new Date())}</span>
                </div>
              </div>
              <form onSubmit={formik.handleSubmit}>
                <div className="d-flex align-items-center justify-content-between p-3">
                  <span className="fw-500">{t("label_opening_balance")}</span>
                  <div className="input-group w-50">
                    <span className="input-group-text" id="basic-addon1">
                      ¥
                    </span>
                    <input
                      type="number"
                      className="form-control no_spring_input"
                      placeholder="Enter Cost Price"
                      aria-label="Username"
                      aria-describedby="basic-addon1"
                      name="openingBalance"
                      value={formik.values.openingBalance}
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      disabled
                    />

                    {formik.touched.openingBalance && formik.errors.openingBalance ? <div className="text-danger ms-3">{formik.errors.openingBalance}</div> : null}
                  </div>
                </div>

                <div className="penomination-table">
                  <table className="w-100">
                    <thead>
                      <tr>
                        <th width="50%">{t("label_jpy_notes")}</th>
                        <th width="50%">{t("label_jpy_coins")}</th>
                      </tr>
                    </thead>
                    <tbody>{generateTableRows()}</tbody>
                  </table>
                </div>
                {(formik.touched.notes && Object.values(formik.errors.notes || {}).some(Boolean)) || (formik.touched.coins && Object.values(formik.errors.coins || {}).some(Boolean)) ? (
                  <div className="text-danger">{t("err_min_note_value")}</div>
                ) : null}
                <div className="text-center mt-3">
                  <button type="submit" className="btn btn-primary">
                    {t("label_confirm")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
